/// <reference types="./docx.d.mts" />
import * as $json from "../../gleam_json/gleam/json.mjs";
import * as $dict from "../../gleam_stdlib/gleam/dict.mjs";
import * as $decode from "../../gleam_stdlib/gleam/dynamic/decode.mjs";
import * as $list from "../../gleam_stdlib/gleam/list.mjs";
import * as $option from "../../gleam_stdlib/gleam/option.mjs";
import { Some } from "../../gleam_stdlib/gleam/option.mjs";
import * as $result from "../../gleam_stdlib/gleam/result.mjs";
import * as $utils from "../common/utils.mjs";
import * as $image from "../data/docx/image.mjs";
import * as $kind from "../data/docx/kind.mjs";
import * as $page_break from "../data/docx/page_break.mjs";
import * as $paragraph from "../data/docx/paragraph.mjs";
import * as $table from "../data/docx/table.mjs";
import * as $block from "../data/proposal_element/block.mjs";
import * as $question from "../data/proposal_element/question.mjs";
import * as $language from "../data/translate/language.mjs";
import * as $iso639 from "../data/translate/language/iso639.mjs";
import { Ok, toList, CustomType as $CustomType } from "../gleam.mjs";

export class Docx extends $CustomType {
  constructor(language, styles, elements) {
    super();
    this.language = language;
    this.styles = styles;
    this.elements = elements;
  }
}

export class PageBreak extends $CustomType {
  constructor($0) {
    super();
    this[0] = $0;
  }
}

export class Paragraph extends $CustomType {
  constructor($0) {
    super();
    this[0] = $0;
  }
}

export class Table extends $CustomType {
  constructor($0) {
    super();
    this[0] = $0;
  }
}

export class Image extends $CustomType {
  constructor($0) {
    super();
    this[0] = $0;
  }
}

export function encode_styles(styles) {
  return $json.object(
    $list.map(
      styles,
      (_use0) => {
        let style = _use0[0];
        let value = _use0[1];
        return [style, $json.string(value)];
      },
    ),
  );
}

export function element_to_html(element) {
  if (element instanceof PageBreak) {
    let page_break = element[0];
    return $page_break.to_html(page_break);
  } else if (element instanceof Paragraph) {
    let paragraph = element[0];
    return $paragraph.to_html(paragraph);
  } else if (element instanceof Table) {
    let table = element[0];
    return $table.to_html(table);
  } else {
    let image = element[0];
    return $image.to_html(image);
  }
}

export function to_html(docx) {
  return $list.map(
    docx.elements,
    (element) => { return element_to_html(element); },
  );
}

export function to_labelling(docx) {
  return $list.map(
    docx.elements,
    (element) => {
      if (element instanceof PageBreak) {
        let page_break = element[0];
        return $page_break.to_html(page_break);
      } else if (element instanceof Paragraph) {
        let paragraph = element[0];
        return $paragraph.to_labelling(paragraph);
      } else if (element instanceof Table) {
        let table = element[0];
        return $table.to_labelling(table);
      } else {
        let image = element[0];
        return $image.to_html(image);
      }
    },
  );
}

export function element_decoder() {
  return $decode.one_of(
    (() => {
      let _pipe = $table.decoder();
      return $decode.map(_pipe, (var0) => { return new Table(var0); });
    })(),
    toList([
      (() => {
        let _pipe = $page_break.decoder();
        return $decode.map(_pipe, (var0) => { return new PageBreak(var0); });
      })(),
      (() => {
        let _pipe = $paragraph.decoder();
        return $decode.map(_pipe, (var0) => { return new Paragraph(var0); });
      })(),
      (() => {
        let _pipe = $image.decoder();
        return $decode.map(_pipe, (var0) => { return new Image(var0); });
      })(),
    ]),
  );
}

export function decoder() {
  return $decode.field(
    "language",
    $iso639.decoder(),
    (language) => {
      return $decode.field(
        "style_html_tag_map",
        (() => {
          let content = $decode.map($decode.dynamic, $utils.to_json);
          return $decode.dict($decode.string, content);
        })(),
        (styles) => {
          return $decode.field(
            "elements",
            $decode.list(element_decoder()),
            (elements) => {
              let styles$1 = $dict.to_list(styles);
              return $decode.success(new Docx(language, styles$1, elements));
            },
          );
        },
      );
    },
  );
}

export function encode_element(element) {
  if (element instanceof PageBreak) {
    let pb = element[0];
    return $page_break.encode(pb);
  } else if (element instanceof Paragraph) {
    let p = element[0];
    return $paragraph.encode(p);
  } else if (element instanceof Table) {
    let t = element[0];
    return $table.encode(t);
  } else {
    let i = element[0];
    return $image.encode(i);
  }
}

export function encode(docx) {
  return $json.object(
    toList([
      ["elements", $json.array(docx.elements, encode_element)],
      ["style_html_tag_map", encode_styles(docx.styles)],
      ["language", $json.string($iso639.to_string(docx.language))],
    ]),
  );
}

export function index(element) {
  if (element instanceof PageBreak) {
    let p = element[0];
    return p.index;
  } else if (element instanceof Paragraph) {
    let p = element[0];
    return p.index;
  } else if (element instanceof Table) {
    let t = element[0];
    return t.index;
  } else {
    let i = element[0];
    return i.index;
  }
}

export function update_kind(element, kind) {
  if (element instanceof PageBreak) {
    let p = element[0];
    let metadata = new $page_break.Metadata(new Some(kind));
    return new PageBreak(
      (() => {
        let _record = p;
        return new $page_break.PageBreak(_record.index, metadata);
      })(),
    );
  } else if (element instanceof Paragraph) {
    let p = element[0];
    let _block;
    let _record = p.metadata;
    _block = new $paragraph.Metadata(
      new Some(kind),
      _record.is_list,
      _record.list_type,
      _record.list_level,
    );
    let metadata = _block;
    return new Paragraph(
      (() => {
        let _record$1 = p;
        return new $paragraph.Paragraph(
          _record$1.index,
          _record$1.tag,
          _record$1.children,
          _record$1.alignment,
          _record$1.original_style,
          metadata,
        );
      })(),
    );
  } else if (element instanceof Table) {
    let t = element[0];
    let metadata = new $table.Metadata(new Some(kind));
    return new Table(
      (() => {
        let _record = t;
        return new $table.Table(
          _record.index,
          _record.tag,
          _record.rows,
          metadata,
        );
      })(),
    );
  } else {
    let i = element[0];
    let metadata = new $image.Metadata(new Some(kind));
    return new Image(
      (() => {
        let _record = i;
        return new $image.Image(_record.index, metadata);
      })(),
    );
  }
}

export function kind(element) {
  let _block;
  if (element instanceof PageBreak) {
    let p = element[0];
    _block = p.metadata.kind;
  } else if (element instanceof Paragraph) {
    let p = element[0];
    _block = p.metadata.kind;
  } else if (element instanceof Table) {
    let t = element[0];
    _block = t.metadata.kind;
  } else {
    let i = element[0];
    _block = i.metadata.kind;
  }
  let _pipe = _block;
  return $option.unwrap(_pipe, new $kind.Other());
}

export function from_block(block) {
  let html = block.content;
  let _block;
  let _pipe = $page_break.from_html_string(html);
  _block = $result.map(_pipe, (var0) => { return new PageBreak(var0); });
  let _pipe$1 = _block;
  let _pipe$2 = $result.lazy_or(
    _pipe$1,
    () => {
      let _pipe$2 = $table.from_html_string(html);
      return $result.map(_pipe$2, (var0) => { return new Table(var0); });
    },
  );
  let _pipe$3 = $result.lazy_or(
    _pipe$2,
    () => {
      let _pipe$3 = $image.from_html_string(html);
      return $result.map(_pipe$3, (var0) => { return new Image(var0); });
    },
  );
  let _pipe$4 = $result.lazy_or(
    _pipe$3,
    () => {
      let _pipe$4 = $paragraph.from_html_string(html);
      return $result.map(_pipe$4, (var0) => { return new Paragraph(var0); });
    },
  );
  return $result.map(_pipe$4, $list.wrap);
}

export function from_question(question) {
  let answer = $option.unwrap(question.answer.custom, "");
  return $result.try$(
    $paragraph.from_html_string(question.content),
    (question) => {
      return $result.try$(
        $paragraph.from_html_string(answer),
        (answer) => {
          return new Ok(
            toList([new Paragraph(question), new Paragraph(answer)]),
          );
        },
      );
    },
  );
}
